import { lazy, Suspense } from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "./components/ui/sonner";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { userLandingPath } from "./utils/userLanding";
import LoginPage from "./pages/LoginPage";
import DashboardLayout from "./components/layout/DashboardLayout";
import "./App.css";

const AdminDashboard = lazy(() => import("./pages/admin/AdminDashboard"));
const DashboardSeguimiento = lazy(() => import("./pages/admin/DashboardSeguimiento"));
const UsersPage = lazy(() => import("./pages/admin/UsersPage"));
const RolesPage = lazy(() => import("./pages/admin/RolesPage"));
const FormSegmentsPage = lazy(() => import("./pages/admin/FormSegmentsPage"));
const FormsPage = lazy(() => import("./pages/admin/FormsPage"));
const FormBuilder = lazy(() => import("./pages/admin/FormBuilder"));
const FormResponses = lazy(() => import("./pages/admin/FormResponses"));
const ResponsesHub = lazy(() => import("./pages/admin/ResponsesHub"));
const FormPreview = lazy(() => import("./pages/admin/FormPreview"));
const FormPowerBI = lazy(() => import("./pages/admin/FormPowerBI"));
const DatabasesPage = lazy(() => import("./pages/admin/DatabasesPage"));
const DatabaseDetail = lazy(() => import("./pages/admin/DatabaseDetail"));
const VisitPlansPage = lazy(() => import("./pages/admin/VisitPlansPage"));
const ItinerariesPage = lazy(() => import("./pages/admin/ItinerariesPage"));
const AdminItineraryDetail = lazy(() => import("./pages/admin/AdminItineraryDetail"));
const UserDashboard = lazy(() => import("./pages/user/UserDashboard"));
const UserForms = lazy(() => import("./pages/user/UserForms"));
const MyItinerary = lazy(() => import("./pages/user/MyItinerary"));
const ItineraryDetail = lazy(() => import("./pages/user/ItineraryDetail"));
const VisitDetail = lazy(() => import("./pages/user/VisitDetail"));
const FillForm = lazy(() => import("./pages/user/FillForm"));
const MyResponses = lazy(() => import("./pages/user/MyResponses"));
const AssistantPage = lazy(() => import("./pages/user/AssistantPage"));

const RouteLoader = () => (
  <div className="min-h-[50vh] flex items-center justify-center">
    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600" />
  </div>
);

const LazyPage = ({ children }) => (
  <Suspense fallback={<RouteLoader />}>
    {children}
  </Suspense>
);

const ProtectedRoute = ({ children, adminOnly = false }) => {
  const { user, isLoading } = useAuth();

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  if (adminOnly && user.role_level > 1) {
    return <Navigate to={userLandingPath(user)} replace />;
  }

  return children;
};

const ItinerariesEnabledRoute = ({ children }) => {
  const { user, isLoading, itinerariesEnabled } = useAuth();

  if (isLoading) return <RouteLoader />;
  if (!user) return <Navigate to="/login" replace />;
  if (user.role_level > 1 && !itinerariesEnabled) return <Navigate to="/dashboard/forms" replace />;
  return children;
};

const RecurringAgendaRoute = ({ children }) => {
  const { user, isLoading, canViewRecurringAgenda } = useAuth();

  if (isLoading) return <RouteLoader />;
  if (!user) return <Navigate to="/login" replace />;
  if (user.role_level > 1 && !canViewRecurringAgenda) return <Navigate to="/dashboard/forms" replace />;
  return children;
};

const TrackingDashboardRoute = ({ children }) => {
  const { user, isLoading, canAccessTrackingDashboard } = useAuth();
  if (isLoading) return <RouteLoader />;
  if (!user) return <Navigate to="/login" replace />;
  if (!canAccessTrackingDashboard) return <Navigate to={userLandingPath(user)} replace />;
  return children;
};

function AppRoutes() {
  const { user, canViewItineraryDashboard } = useAuth();

  return (
    <Routes>
      <Route path="/login" element={<LoginPage />} />

      {/* Admin Routes */}
      <Route
        path="/admin"
        element={
          <ProtectedRoute adminOnly>
            <DashboardLayout />
          </ProtectedRoute>
        }
      >
        <Route index element={<LazyPage><AdminDashboard /></LazyPage>} />
        <Route path="dashboard-seguimiento" element={<TrackingDashboardRoute><LazyPage><DashboardSeguimiento /></LazyPage></TrackingDashboardRoute>} />
        <Route path="users" element={<LazyPage><UsersPage /></LazyPage>} />
        <Route path="roles" element={<LazyPage><RolesPage /></LazyPage>} />
        <Route path="form-segments" element={<LazyPage><FormSegmentsPage /></LazyPage>} />
        <Route path="forms" element={<LazyPage><FormsPage /></LazyPage>} />
        <Route path="forms/new" element={<LazyPage><FormBuilder /></LazyPage>} />
        <Route path="forms/:formId/edit" element={<LazyPage><FormBuilder /></LazyPage>} />
        <Route path="forms/:formId/preview" element={<LazyPage><FormPreview /></LazyPage>} />
        <Route path="forms/:formId/responses" element={<LazyPage><FormResponses /></LazyPage>} />
        <Route path="forms/:formId/power-bi" element={<LazyPage><FormPowerBI /></LazyPage>} />
        <Route path="databases" element={<LazyPage><DatabasesPage /></LazyPage>} />
        <Route path="databases/:databaseId" element={<LazyPage><DatabaseDetail /></LazyPage>} />
        <Route path="visit-plans" element={<LazyPage><VisitPlansPage /></LazyPage>} />
        <Route path="itineraries" element={<LazyPage><ItinerariesPage /></LazyPage>} />
        <Route path="itineraries/:itineraryId" element={<LazyPage><AdminItineraryDetail /></LazyPage>} />
        <Route path="responses" element={<LazyPage><ResponsesHub /></LazyPage>} />
        <Route path="assistant" element={<LazyPage><AssistantPage /></LazyPage>} />
      </Route>

      {/* User Routes */}
      <Route
        path="/dashboard"
        element={
          <ProtectedRoute>
            <DashboardLayout />
          </ProtectedRoute>
        }
      >
        <Route index element={canViewItineraryDashboard ? <LazyPage><UserDashboard /></LazyPage> : <Navigate to="/dashboard/forms" replace />} />
        <Route path="forms" element={<LazyPage><UserForms /></LazyPage>} />
        <Route path="itinerary" element={<RecurringAgendaRoute><LazyPage><MyItinerary /></LazyPage></RecurringAgendaRoute>} />
        <Route path="itinerary/:itineraryId" element={<ItinerariesEnabledRoute><LazyPage><ItineraryDetail /></LazyPage></ItinerariesEnabledRoute>} />
        <Route path="visits/:visitId" element={<ItinerariesEnabledRoute><LazyPage><VisitDetail /></LazyPage></ItinerariesEnabledRoute>} />
        <Route path="visits/:visitId/activities/:activityId/form/:formId" element={<ItinerariesEnabledRoute><LazyPage><FillForm /></LazyPage></ItinerariesEnabledRoute>} />
        <Route path="form/:formId" element={<LazyPage><FillForm /></LazyPage>} />
        <Route path="my-responses" element={<LazyPage><MyResponses /></LazyPage>} />
        <Route path="dashboard-seguimiento" element={<TrackingDashboardRoute><LazyPage><DashboardSeguimiento /></LazyPage></TrackingDashboardRoute>} />
        <Route path="assistant" element={<LazyPage><AssistantPage /></LazyPage>} />
        <Route path="responses" element={<LazyPage><ResponsesHub /></LazyPage>} />
        <Route path="responses/:formId" element={<LazyPage><FormResponses /></LazyPage>} />
      </Route>

      {/* Redirect */}
      <Route
        path="/"
        element={
          user ? (
            <Navigate to={userLandingPath(user)} replace />
          ) : (
            <Navigate to="/login" replace />
          )
        }
      />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <AppRoutes />
        <Toaster position="top-right" richColors />
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
